import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Scale, Loader2, ShieldCheck } from 'lucide-react';
import { API, useAuth } from '../../App';
import { useMyAccess } from '../../hooks/useMyAccess';
import { useLegalFormOptions } from './useLegalForm';
import { LegalFormPicker } from './LegalFormPicker';
import { PartnersEditor, emptyPartner, partnersTotal } from './PartnersEditor';

const SKIP_PREFIXES = ['/onboarding', '/auth', '/signup', '/login', '/portal', '/help', '/privacy', '/terms', '/pricing', '/contact', '/close-account', '/admin', '/partner', '/partners', '/r/'];

export default function LegalFormConfirmGate() {
  const { user, business, businessResolved, refreshBusiness } = useAuth();
  const { access } = useMyAccess();
  const location = useLocation();
  const options = useLegalFormOptions(business?.country);
  const [form, setForm] = useState('');
  const [partners, setPartners] = useState([emptyPartner(), emptyPartner()]);
  const [saving, setSaving] = useState(false);

  const isBusiness = (user?.account_type || 'business') !== 'personal';
  const skip = location.pathname === '/' || SKIP_PREFIXES.some((p) => location.pathname.startsWith(p));
  const needed = !!user && isBusiness && businessResolved && !!business?.id && business?.legal_form_confirmed !== true;
  const isOwner = !!access?.is_owner;
  if (!needed || skip || !isOwner) return null;

  const target = options?.forms?.find((f) => f.code === form);
  const partnersOk = !target?.partners_required || (partners.length >= 2 && Math.abs(partnersTotal(partners) - 100) < 0.01);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { legal_form: form };
      if (target?.partners_required) body.partners = partners.map((p) => ({ ...p, profit_share_pct: parseFloat(p.profit_share_pct) || 0, email: p.email || null }));
      await axios.post(`${API}/business/legal-form/confirm`, body);
      toast.success('Legal form confirmed - your tax computation now follows it');
      await refreshBusiness?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the legal form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-lg [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-testid="legal-form-confirm-gate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><Scale className="w-5 h-5 text-[#283F8B]" /> Confirm your legal form</DialogTitle>
          <DialogDescription>
            {business?.business_name} needs its legal form on record so we tax profits correctly, prepare the right return, and use the right equity accounts and deadlines.
            Tax pages stay locked until this is set. Only the owner can do this, and later changes require your password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <LegalFormPicker options={options} value={form} onChange={setForm} testId="legal-form-gate-select" className="h-12" />
          {target?.partners_required && <PartnersEditor partners={partners} onChange={setPartners} />}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> Recorded in your audit trail with today's date as the effective date.
          </div>
          <Button onClick={submit} disabled={!form || !partnersOk || saving} className="w-full h-11 bg-[#283F8B] hover:bg-[#1f3270]" data-testid="legal-form-gate-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm and continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
